import {
  compareAsc,
  format,
  formatDistanceToNow,
  isAfter,
  isBefore,
  isValid,
  parseISO,
} from 'date-fns';
import * as Sentry from '@sentry/react-native';
import {
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import Head from 'expo-router/head';
import { useHeaderHeight } from 'expo-router/react-navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { View, XStack, YStack } from 'tamagui';

import Carousel from '@/components/Carousel';
import DateSelector from '@/components/DateSelector';
import EventDetailMeta from '@/components/EventDetailMeta';
import EventDetailSkeleton from '@/components/EventDetailSkeleton';
import EventErrorPage from '@/components/EventErrorPage';
import ExternalTicketTier from '@/components/ExternalTicketTier';
import ImageCarousel from '@/components/ImageCarousel';
import MembersOnlyTicketTierGroup from '@/components/MembersOnlyTierGroup';
import { PSHeaderBackButtonOnTransparentHeader } from '@/components/PSHeaderButtons';
import SocialLinkSheet from '@/components/SocialLinkSheet';
import TicketTier from '@/components/TicketTier';
import TimeSelector from '@/components/TimeSelector';
import PSBottomBar from '@/components/ui/PSBottomBar';
import PSButton from '@/components/ui/PSButton';
import PSCircleButton from '@/components/ui/PSCircleButton';
import PSIcon from '@/components/ui/PSIcon';
import PSRichText from '@/components/ui/PSRichText';
import PageScrollView from '@/components/ui/PageScrollView';
import { space } from '@/constants/tokens';
import { asApiError } from '@/src/api/apiError';
import authApi from '@/src/api/authApi';
import type { BookingSlot } from '@/src/api/bookingsApi';
import {
  PSPrice,
  type Event,
  type EventSlot,
  type EventSlotTicketTier,
} from '@/src/api/eventsApi';
import { useMembershipPlans } from '@/src/hooks/useMembershipPlans';
import { usePastMemberships } from '@/src/hooks/usePastMemberships';
import { usePendingMembership } from '@/src/hooks/usePendingMembership';
import { useEventCart } from '@/src/lib/eventCart';
import { derivePlanToUpsellInMembersOnlyTicketTierGroup } from '@/src/lib/membershipUpsell';
import type { Metadata } from 'expo-server';

import AccessPassBanner from '@/components/AccessPassBanner';
import PSBadge from '@/components/ui/PSBadge';
import PSText from '@/components/ui/PSText';
import { setRestoredUser, useSessionUser } from '@/src/lib/sessionStore';
import { showAlert } from '@/src/utils/alert';
import { openURL, webUrl } from '@/src/utils/linkingUtils';
import { shareLink } from '@/src/utils/shareUtils';
import { normalizeSocialLink } from '@/src/utils/validation';
import { toast } from '@tamagui/toast/v2';
import { StatusBar } from 'expo-status-bar';
import PSBookedBanner from '@/components/PSBookedBanner';

/**
 * The event-detail page: `app/event/[id]/index.tsx`.
 *
 * The event is NOT fetched here — it's read from `useEventCart`, which owns the
 * fetch for both of an event's routes (see `src/lib/eventCart`), so pushing to
 * `/confirm` and coming back costs no request. Three states off it: loading
 * shows `EventDetailSkeleton` (the page's shape, pulsing) so the real screen
 * slots in without a jump; error is `EventErrorPage` (the failure's own message
 * + a Retry that re-runs the fetch); success is the full screen, `EventBody`.
 *
 * The screen body is INLINE here (not a shared `EventDetail` component) so it can
 * read that same cart directly rather than drilling the selection + setters
 * through a prop boundary. "Book now" pushes to the confirm step.
 */

/** The wire fields `generateMetadata` needs — snake_case, unrelated to `Event`. */
type EventMetaFields = {
  title: string;
  description: string;
  images: { url: string; is_primary: boolean }[];
};

/**
 * SSR-only hook the server calls BEFORE rendering, injecting the result
 * straight into the response's `<head>` — unlike the `<Head>` tags below,
 * which only reach the DOM once client JS runs. Link-preview crawlers
 * (Facebook, Twitter/X, Slack, iMessage) fetch the raw HTML and never run
 * JS, so this is the only thing they ever see.
 *
 * A plain unauthenticated `fetch`, not `eventsApi` — that goes through
 * `api.ts`'s per-request `getAuthToken()` read, which on web is AsyncStorage's
 * `localStorage`-backed implementation and has no server/Node implementation.
 * Failing closed (`null`) on any error so a bad response costs this page its
 * previews, not its render.
 */
export async function generateMetadata(
  _request: unknown,
  params: Record<string, string | string[]>
): Promise<Metadata | null> {
  const id = Array.isArray(params.id) ? params.id[0] : params.id;
  if (!id) return null;

  try {
    const response = await fetch(
      `${process.env.EXPO_PUBLIC_API_BASE_URL}/events/${id}`
    );
    if (!response.ok) return null;
    const event: EventMetaFields = await response.json();

    const description = toMetaDescription(event.description);
    const imageUrl =
      event.images.find((image) => image.is_primary)?.url ??
      event.images[0]?.url;
    const eventUrl = webUrl(`/events/${id}`);

    return {
      title: `${event.title} · Putting Scene`,
      description,
      openGraph: {
        title: event.title,
        description,
        url: eventUrl,
        type: 'website',
        images: imageUrl ? [imageUrl] : undefined,
      },
      twitter: {
        card: 'summary_large_image',
        title: event.title,
        description,
        images: imageUrl ? [imageUrl] : undefined,
      },
    };
  } catch {
    return null;
  }
}

export default function EventPage() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { eventState } = useEventCart();

  if (eventState.status === 'loading') {
    return <EventDetailSkeleton />;
  }

  if (eventState.status === 'error') {
    // `message` is already the words for this failure — the data layer picks
    // them per kind (see `src/api/apiError`), so the screen doesn't.
    return (
      <EventErrorPage
        message={eventState.error.message}
        onRetry={eventState.retry}
      />
    );
  }

  return <EventBody event={eventState.event} eventId={id} />;
}

/**
 * The event-detail body — render + all the domain logic — for one fetched event.
 *
 * `EventBody` owns ALL the domain logic; the components it composes are
 * render-only and are handed visual flags (see `docs/building-components.md`).
 * The non-obvious rules, all derived here:
 *
 *   - lifecycle: `event.isPast` is an API-provided flag (the screen never
 *     re-derives it). It alone ⇒ not ticketable: no picker, no tickets, just a
 *     hero badge ("Missed out"). `event.isSoldOut` gets the same hero badge
 *     ("Sold out") but does NOT hide the picker or the tier list — a sold-out
 *     event's tiers still render, each showing its own "Sold out" badge in
 *     place of the stepper (that's per-tier `bookability`, not this flag).
 *   - the slot↔tier junction: the tiers on sale, and their per-slot sold-out
 *     state, come from `slotTiers` (via the fixtures helpers), NOT from the tier
 *     list alone — the applicable set differs per slot.
 *   - blocking is per-TICKET-TIER (never event-level) and it's the SERVER's
 *     call: each tier arrives with a `bookability` verdict and one ranked
 *     reason, which TicketTier draws (login / get-pass / limit notice). The
 *     booking bar has no gate branch — precedence is external → empty-cart →
 *     book.
 *
 * The date/time picker groups slots by calendar date: a date can hold several
 * time slots (a multi-session day), shown as time chips under the chosen date.
 *
 * The cart (picked slot + per-tier quantities) is READ FROM the shared
 * `useEventCart` context, not local state, so it persists across the trip to the
 * confirm step and back — and the ticket count + total on the booking bar come
 * from there too, already added up (see `src/lib/eventCart`).
 */
function EventBody({ event, eventId }: { event: Event; eventId: string }) {
  const user = useSessionUser();
  const router = useRouter();
  const userExists = user !== null;
  const headerHeight = useHeaderHeight();

  const isExternal = event.externalUrl !== null;
  const isTicketable = !isExternal && !event.isInPast;
  let heroBadgeLabel = null;
  if (event.isInPast) {
    heroBadgeLabel = 'In the past';
  }
  // if an event is both sold out and in past then sold out badge wins
  if (event.isSoldOut) {
    heroBadgeLabel = 'Sold out';
  }

  const slots = useMemo(
    () =>
      event.slots
        .filter((slot) => !slot.isInPast)
        .sort((a, b) => a.startAt.localeCompare(b.startAt)),
    [event]
  );

  // The ONE booking the banner speaks for, and the phase it speaks in.
  //
  // The phase is the booked SLOT's, never `event.isInPast` — that flips only
  // once every slot has passed, so on a multi-day event it can't say anything
  // about the one slot this user booked. Judged on the slot's END (as
  // `/delete-account` judges a reservation): a slot that started an hour ago is
  // still one the user can walk into.
  //
  // Ranked live → upcoming → past, so a slot happening RIGHT NOW outranks one
  // next week, and a past booking surfaces only when the user has nothing else
  // for this event. Note the tie-break flips per bucket: the nearest one AHEAD
  // among live/upcoming, the nearest one BEHIND (most recent) among past.
  const bannerBooking = useMemo(() => {
    // One `now` for the whole pass, so two bookings can't be classified against
    // clocks a few ms apart.
    const now = new Date();
    // A slot with an unparseable/missing date would otherwise misclassify as
    // 'live' (isBefore/isAfter return false on an Invalid Date, they don't
    // throw) and then crash the banner's render, which does throw on one.
    // Drop it instead — one bad booking shouldn't blank the whole page.
    const phaseOf = (slot: BookingSlot): BookingPhase | null => {
      const start = parseISO(slot.startDatetime);
      const end = parseISO(slot.endDatetime);
      if (!isValid(start) || !isValid(end)) return null;
      return isBefore(end, now)
        ? 'past'
        : isAfter(start, now)
          ? 'upcoming'
          : 'live';
    };

    const byStart = (a: RankedBooking, b: RankedBooking) =>
      compareAsc(
        parseISO(a.booking.slot.startDatetime),
        parseISO(b.booking.slot.startDatetime)
      );

    const ranked = event.bookings.flatMap((booking) => {
      const phase = booking.slot ? phaseOf(booking.slot) : null;
      if (!phase) {
        Sentry.captureException(
          new Error('Booking has unparseable slot dates'),
          {
            fingerprint: ['booked-banner', 'invalid-slot-dates'],
            extra: { bookingId: booking.id, slot: booking.slot },
          }
        );
        return [];
      }
      return [{ booking, phase }];
    });

    return (
      ranked.sort(
        (a, b) =>
          PHASE_RANK[a.phase] - PHASE_RANK[b.phase] ||
          (a.phase === 'past' ? byStart(b, a) : byStart(a, b))
      )[0] ?? null
    );
  }, [event.bookings]);

  const {
    eventState,
    selectedSlot,
    selectSlot,
    quantityOf,
    changeQuantity,
    totalTickets,
    totalDisplay,
    approvalTier,
    socialLink,
    setSocialLink,
  } = useEventCart();
  useEffect(() => {
    if (selectedSlot === null && slots.length > 0) {
      const firstBookableSlot = slots.find((slot) =>
        slot.ticketTiers.some((tier) => tier.bookability.isBookable)
      );
      selectSlot((firstBookableSlot ?? slots[0]).id);
    }
  }, [selectedSlot, slots, selectSlot]);

  // A single booking has a ticket of its own to open; several have no one
  // screen, so the banner lands on the profile's list — the only place that
  // shows them all — scrolled onto the one the banner is about.
  const onViewBookings = useCallback(() => {
    if (!bannerBooking) return;
    if (event.bookings.length === 1) {
      router.push(`/bookings/${bannerBooking.booking.id}`);
      return;
    }
    router.push({
      pathname: '/profile',
      params: { bookingId: bannerBooking.booking.id },
    });
  }, [bannerBooking, event.bookings.length, router]);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const { refresh } = eventState;
  const onRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await refresh();
    setIsRefreshing(false);
  }, [refresh]);

  // Tiers on sale at the selected slot (the junction — differs per slot).
  const ticketTiersForSelectedSlot = useMemo(
    () => (selectedSlot ? selectedSlot.ticketTiers : []),
    [selectedSlot]
  );
  const openTiers = ticketTiersForSelectedSlot.filter(
    (tier) => !isMembersOnlyTier(tier)
  );
  const membersOnlyTiers = ticketTiersForSelectedSlot.filter(isMembersOnlyTier);

  const { plans } = useMembershipPlans();
  const pastMemberships = usePastMemberships();
  const pendingMembership = usePendingMembership();
  // Held back until the history lands, or an ex-member sees the trial pitched
  // and then swapped out.
  const membersOnlyPlan = derivePlanToUpsellInMembersOnlyTicketTierGroup({
    plans: (pastMemberships && plans) ?? [],
    user,
    pastMemberships: pastMemberships ?? [],
    hasNoTrialTicketTiers: !membersOnlyTiers.some((t) =>
      t.membershipsAppliedToTickettier?.some((t) => t.kind === 'trial')
    ),
  });

  // Started but not yet `isInPast` (that flips only once it ENDS) — still
  // offered, but "Book now" should warn before booking into it.
  const isSelectedSlotInProgress =
    selectedSlot !== null &&
    !selectedSlot.isInPast &&
    parseISO(selectedSlot.startAt) <= new Date();

  // ── Resuming a booking after an auth detour ─────────────────────────────
  // Set true right when "Book now" pushes into the auth flow to fix an
  // incomplete profile — that tap IS the booking intent, so it shouldn't be
  // asked for twice. Once the auth modal closes (this screen regains focus)
  // with a now-complete profile, push straight to confirm instead of making
  // the user find and press Book now again.
  //
  // `useFocusEffect` (not a plain effect): the profile can also become
  // complete while this screen ISN'T focused — e.g. via the profile screen's
  // own "complete your profile" link — and firing then would be silently
  // wrong. `pendingBooking` is consumed on the FIRST refocus after the
  // detour regardless of outcome, so an abandoned detour can't leave it
  // sitting armed to fire on some later, unrelated refocus.
  const [pendingBooking, setPendingBooking] = useState(false);
  useFocusEffect(
    useCallback(() => {
      if (!pendingBooking) return;
      // don't send to confirm is social media link is pending
      const userNeedsToSubmitSocialLink =
        approvalTier?.requiresSocialLink && !socialLink;
      if (user?.isProfileComplete && !userNeedsToSubmitSocialLink) {
        setPendingBooking(false);
        router.push(`/events/${eventId}/confirm`);
      }
    }, [
      pendingBooking,
      user?.isProfileComplete,
      eventId,
      router,
      approvalTier,
      socialLink,
    ])
  );

  // ── Approval-only tiers: the social-link sheet ──────────────────────────
  // `approvalTier` is already IN the cart by the time "Book now" can be
  // pressed (`eventCart` derives it onto the cart the moment its slot is
  // selected) — this sheet only stands between the tap and confirm when that
  // tier also asks for a link and none has been given yet (see the
  // booking-bar fork below). Submitting a valid one both stores it and
  // continues the tap that was blocked, so the user doesn't have to press
  // "Book now" a second time.
  const [socialSheetOpen, setSocialSheetOpen] = useState(false);
  const [socialLinkDraft, setSocialLinkDraft] = useState('');
  const [socialLinkError, setSocialLinkError] = useState<string | undefined>();
  const [isSavingSocialLink, setIsSavingSocialLink] = useState(false);

  // A user who has shared a link before gets it back in the field — the ask is
  // then a confirm, not a re-type. Seeded on OPEN rather than from a `useState`
  // initializer because the profile can arrive after this screen mounts (the
  // login detour above is a modal over it, so there's no remount to re-seed).
  // EventBody never compiles (React Compiler can't lower the try/finally below),
  // so its memo-preservation verdicts here are noise.
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const openSocialSheet = useCallback(() => {
    setSocialLinkDraft(user?.socialMediaLink ?? '');
    setSocialLinkError(undefined);
    setSocialSheetOpen(true);
  }, [user?.socialMediaLink]);

  // The link lands on the PROFILE as well as the cart: it's the user's one
  // social link, not this booking's answer, so `PATCH /me` is what makes the
  // next approval-only event prefill instead of ask. Confirm is pushed only
  // once that save returns — a 4xx here is the server's verdict on the URL we
  // sent (its only check is "is a URL") and belongs under the field, while a
  // network/5xx failure isn't about the link at all and goes to a toast.
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const onSubmitSocialLink = useCallback(async () => {
    const normalized = normalizeSocialLink(socialLinkDraft);
    if (!normalized) {
      setSocialLinkError(
        'Enter a valid Instagram, X, or LinkedIn profile link.'
      );
      return;
    }
    setIsSavingSocialLink(true);
    try {
      const { user: updated } = await authApi.updateMe({
        socialMediaLink: normalized,
      });
      setRestoredUser(updated);
      setSocialLink(normalized);
      setSocialSheetOpen(false);
      router.push(`/events/${eventId}/confirm`);
    } catch (caught) {
      const failure = asApiError(caught);
      if (failure.kind === 'api') setSocialLinkError(failure.message);
      else toast.error(failure.message);
    } finally {
      setIsSavingSocialLink(false);
    }
  }, [socialLinkDraft, setSocialLink, router, eventId]);

  // Whether a tier can be booked AT THIS SLOT — and if not, why — is the
  // server's answer, already ranked (see `TierBookability`), passed straight
  // through.
  const renderTier = (tier: EventSlotTicketTier, surface: 'light' | 'dark') => (
    <TicketTier
      key={tier.slotTierId}
      surface={surface}
      name={tier.name}
      price={tier.priceWithTax}
      description={tier.description}
      membershipsAppliedToTickettier={tier.membershipsAppliedToTickettier}
      bookability={tier.bookability}
      requiresGuestApproval={tier.requiresGuestApproval}
      participationRequest={tier.participationRequest}
      quantity={quantityOf(tier.slotTierId)}
      numTickets={Math.min(tier.maxSeatsPerUser, tier.availableCapacity)}
      onDecrement={() => changeQuantity(tier, -1)}
      onIncrement={() => changeQuantity(tier, +1)}
      onViewBookings={(bookings) => {
        router.push(`/bookings/${bookings[0].id}`);
      }}
      pendingMembership={pendingMembership}
    />
  );

  // ── Booking bar ─────────────────────────────────────────────────────────
  // The SCREEN owns the logic and picks WHICH bar to render; the bars themselves
  // are render-only. An event is external or ticketed (known up top), so they're
  // two separate components, not one that branches internally:
  //   • external → ExternalBookingBar ("Get Tickets" → the source link)
  //   • ticketed → TicketedBookingBar (count + price; "Book Now"/"Login & Book")
  // A past event shows neither (not ticketable). No members-only branch here —
  // that gate lives inside the gated TicketTier card's upsell.
  //
  // The ticketed bar appears only once there's something IN the cart: with no
  // tickets there's no count and no total to state, so the bar would be a CTA
  // with nothing behind it. Adding the first ticket brings it up. Where its CTA
  // then sends the user depends on auth, and the screen owns that fork.
  let bookingBar = null;
  if (isTicketable) {
    if (totalTickets === 0 && !userExists) {
      bookingBar = (
        <PSBottomBar>
          <PSButton
            fullWidth
            // No tickets picked yet, so there's nothing to resume into on
            // return (unlike the detours below) — just carry `next: 'email'`
            // so a fresh signup chains through email instead of leaving them
            // to hit the SAME ask again the moment they press Book now.
            onPress={() =>
              router.push({
                pathname: '/auth/mobile',
                params: { next: 'email' },
              })
            }
            title={event.isRsvpEvent ? 'Login to RSVP' : 'Login to book'}
          />
        </PSBottomBar>
      );
    } else if (totalTickets !== 0) {
      // Where the CTA sends the user, most-complete state first. A finished
      // profile books; anything short of one detours into the auth flow at the
      // step that fixes it, carrying a heading that says why they're there. A
      // missing name chains all the way to email (`next: 'email'`) rather than
      // stopping at the name — this detour needs FULL completion, not just a
      // name, unlike a fresh signup.
      //
      // The detour itself no longer costs a second tap: pressing "Book now"
      // ALREADY stated the booking intent, so `pendingBooking` remembers it
      // and the focus effect below resumes straight to confirm once the auth
      // modal closes with a complete profile — no need to press Book now
      // again. See that effect for the resume.
      let onBook = () => router.push(`/events/${eventId}/confirm`);
      if (user?.areDetailsMissing)
        onBook = () => {
          setPendingBooking(true);
          router.push({
            pathname: '/auth/details',
            params: {
              headerCopy: 'Please complete your profile to make a booking',
              next: 'email',
            },
          });
        };
      else if (user?.isEmailMissing || user?.isOnlyEmailVerificationPending)
        onBook = () => {
          setPendingBooking(true);
          router.push({
            pathname: '/auth/email',
            params: {
              headerCopy: 'Please add and verify your email to make a booking',
            },
          });
        };
      // Profile's complete — last gate before confirm is the approval tier's
      // own ask, if it has one and it hasn't been answered yet this visit.
      else if (approvalTier?.requiresSocialLink && !socialLink)
        onBook = openSocialSheet;
      if (isSelectedSlotInProgress) onBook = warnSlotInProgress(onBook);
      bookingBar = (
        <TicketedBookingBar
          totalTickets={totalTickets}
          priceDisplay={totalDisplay}
          buttonTitle={event.isRsvpEvent ? 'RSVP' : 'Book now'}
          // Logged out → open the auth flow; logged in → the confirm step.
          onBook={onBook}
        />
      );
    }
  }

  // Back/share are filled `PSCircleButton`s carrying a `PSIcon` glyph — one
  // identical control set on every platform (defined once here, wired into the
  // header two ways below). The filled `$background` circle gives the glyph
  // contrast against the hero image.
  //
  // Share hands out the PUBLIC web link for this event, not a deep link: it has
  // to open for whoever receives it, app installed or not, and the web route is
  // the same `/events/[id]` this screen is.
  //
  // A browser with no share sheet copies instead (see `shareLink`), which is
  // invisible on its own — so the glyph flips to a tick for a moment to say it
  // landed. Native never takes this branch; the share sheet is its own receipt.
  const [didCopyLink, setDidCopyLink] = useState(false);
  const onShare = useCallback(async () => {
    const outcome = await shareLink(webUrl(`/events/${eventId}`), event.title);
    if (outcome !== 'copied') return;
    setDidCopyLink(true);
    setTimeout(() => setDidCopyLink(false), 2000);
  }, [eventId, event.title]);

  const shareButton = (
    <PSCircleButton variant="normal" onPress={onShare} filled>
      <PSIcon name={didCopyLink ? 'checkmark' : 'share-outline'} size={18} />
    </PSCircleButton>
  );

  // For link previews (WhatsApp, iMessage, Slack, Twitter/X) when this page's
  // URL is shared — the same PUBLIC link `onShare` hands out above.
  const eventUrl = webUrl(`/events/${eventId}`);
  const metaDescription = toMetaDescription(event.description);
  const metaImageUrl =
    event.images.find((image) => image.isPrimary)?.url ?? event.images[0]?.url;

  return (
    <>
      <Head>
        <title>{event.title} · Putting Scene</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={event.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={eventUrl} />
        {metaImageUrl && <meta property="og:image" content={metaImageUrl} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={event.title} />
        <meta name="twitter:description" content={metaDescription} />
        {metaImageUrl && <meta name="twitter:image" content={metaImageUrl} />}
      </Head>
      <StatusBar style="dark" />
      <Stack.Screen
        options={{
          headerTransparent: true,
          headerTitle: () => <></>,
          headerLeft: () => <PSHeaderBackButtonOnTransparentHeader />,
          unstable_headerLeftItems: () => [
            {
              type: 'custom',
              element: <PSHeaderBackButtonOnTransparentHeader />,
              hidesSharedBackground: true,
            },
          ],

          headerRight: () => shareButton,
          unstable_headerRightItems: () => [
            {
              type: 'custom',
              element: shareButton,
              hidesSharedBackground: true,
            },
          ],
        }}
      />

      <PageScrollView isRefreshing={isRefreshing} onRefresh={onRefresh}>
        <YStack gap="$space.xl">
          {/* `heroBadgeLabel` ("Missed out" / "Sold out") signals a past /
              sold-out event; either state also sends the hero monotone via
              `grayScale`, matching the `EventCard` grayscale treatment.

              The wrapper's negative margins cancel PageScrollView's own gutter
              (`px="$lg"`) and under-header top offset (`pt`) for JUST this one
              child, so the image bleeds edge-to-edge and sits behind the
              transparent header — nothing else in the frame changes. */}
          <View mx={-space.lg} mt={-(headerHeight + space.xl)}>
            <ImageCarousel
              images={event.images.map((i) => i.url)}
              badgeLabel={heroBadgeLabel ?? undefined}
              badgeVariant="outline"
              badgeSize="large"
              grayScale={event.isSoldOut || event.isInPast}
            />
          </View>

          {event.category === 'preview' && (
            <PSBadge label="Preview" variant="neutral" />
          )}
          {event.category === 'ps_original' && (
            <PSBadge label="PS Original" variant="neutral" />
          )}

          <EventDetailMeta event={event} />

          {/* The CTA stays plural on the TOTAL count even though one booking is
              shown: it opens the profile list, which does hold them all. */}
          {bannerBooking && (
            <PSBookedBanner
              {...bookedBannerCopy(
                bannerBooking.phase,
                bannerBooking.booking.slot
              )}
              ctaTitle={`View Booking(s)`}
              onPress={onViewBookings}
              variant={bannerBooking.phase === 'past' ? 'gray' : 'default'}
            />
          )}

          {/* About */}
          <YStack gap="$space.xs">
            <PSText.H1>About</PSText.H1>
            <PSRichText markdown={event.description} />
          </YStack>

          {/* Date/time picker + ticket list only for a bookable (ticketable)
              event. A PAST event shows neither — the hero badge is the whole
              story; an EXTERNAL one shows neither either, but it isn't left
              empty: the `ExternalTicketTier` section above stands in for both.
              A SOLD-OUT event still shows both: each tier's own bookability
              draws its "Sold out" badge, so there's no need to hide the
              section here too. */}
          {isTicketable && slots.length > 0 ? (
            <YStack gap="$space.xl">
              <DateTimePicker
                slots={slots}
                selectedSlot={selectedSlot}
                onSelectSlot={(slot) => selectSlot(slot.id)}
              />
            </YStack>
          ) : null}

          {/* An EXTERNAL event has no tiers to list (the wire sends none), so
              its Tickets section is the one `ExternalTicketTier` card: the
              price we know plus the way out to whoever does sell it. Gated on
              `isExternal` alone, exactly like the external booking bar below,
              so the two can never disagree about whether this event is still
              worth pointing at. */}
          {isExternal && !event.isInPast ? (
            <YStack gap="$sm">
              <PSText.H1>Tickets</PSText.H1>
              <ExternalTicketTier
                displayPrice={event.priceFrom.display}
                url={event.externalUrl!}
              />
            </YStack>
          ) : null}

          {isTicketable && selectedSlot ? (
            <YStack gap="$sm">
              <PSText.H1>Tickets</PSText.H1>
              {openTiers.map((tier) => renderTier(tier, 'light'))}
              {membersOnlyTiers.length > 0 ? (
                // Cancels PageScrollView's gutter, like the hero, so the band
                // runs edge to edge.
                <View mx={-space.lg}>
                  <MembersOnlyTicketTierGroup
                    upsellPlan={membersOnlyPlan}
                    upsellPlanHeaderText={
                      membersOnlyPlan?.kind === 'trial'
                        ? 'Try it before you buy it'
                        : undefined
                    }
                    onSelectPlan={() =>
                      router.push({
                        pathname: '/memberships',
                        params: { section: 'plans' },
                      })
                    }
                  >
                    {membersOnlyTiers.map((tier) => renderTier(tier, 'dark'))}
                  </MembersOnlyTicketTierGroup>
                </View>
              ) : null}
            </YStack>
          ) : null}

          {/* Terms */}
          {event.termsAndConditions ? (
            <YStack gap="$sm">
              <PSText.H1>Terms &amp; conditions</PSText.H1>
              <PSRichText markdown={event.termsAndConditions} />
            </YStack>
          ) : null}
          {/* The members-only band already upsells the pass. */}
          {!user?.membership &&
            !pendingMembership &&
            !(isTicketable && membersOnlyTiers.length > 0) && (
              <View py={'$space.xl'}>
                <AccessPassBanner onPress={() => router.push('/memberships')} />
              </View>
            )}
        </YStack>
      </PageScrollView>

      {bookingBar}

      {socialSheetOpen ? (
        <SocialLinkSheet
          onClose={() => setSocialSheetOpen(false)}
          link={socialLinkDraft}
          ctaCopy="Submit social link"
          onChangeLink={(value) => {
            setSocialLinkDraft(value);
            setSocialLinkError(undefined);
          }}
          onSubmit={onSubmitSocialLink}
          error={socialLinkError}
          isSubmitting={isSavingSocialLink}
          copy={`${event.orgDetails?.name ?? 'The organizer'} curates every guest list. Share your Instagram, LinkedIn, or X (Twitter) profile to help us review your RSVP.`}
        />
      ) : null}
    </>
  );
}

/** Where a booked slot sits against now — see `bannerBooking`. */
type BookingPhase = 'live' | 'upcoming' | 'past';

type RankedBooking = {
  booking: Event['bookings'][number];
  phase: BookingPhase;
};

const PHASE_RANK: Record<BookingPhase, number> = {
  live: 0,
  upcoming: 1,
  past: 2,
};

/** The booked banner's words for each phase. */
function bookedBannerCopy(phase: BookingPhase, slot: BookingSlot) {
  switch (phase) {
    case 'live':
      return {
        title: 'Event is live!',
        subtitle: `Ends in ${formatDistanceToNow(parseISO(slot.endDatetime))}`,
      };
    case 'upcoming':
      return {
        title: 'Event booked!',
        subtitle: `Coming up in ${formatDistanceToNow(
          parseISO(slot.startDatetime)
        )}`,
      };
    case 'past':
      return {
        title: 'You had a booking.',
        subtitle: 'The slot you booked has passed',
      };
  }
}

// Interposed in front of `onBook` when the selected slot is already in
// progress — `proceed` is whichever destination that tap would otherwise
// have gone to (confirm, an auth detour, the social-link sheet).
function warnSlotInProgress(proceed: () => void) {
  return () =>
    showAlert(
      'This slot has already started!',
      "You're booking a slot that's already in progress. Are you sure you want to book this?",
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Yes, book anyway', onPress: proceed },
      ]
    );
}

// A social-share preview can't render markdown — just words. Strips the
// syntax our copy actually uses (headings, list markers, emphasis, links) and
// caps the length for platforms that don't truncate their own og:description.
const META_DESCRIPTION_MAX = 200;

function toMetaDescription(markdown: string): string {
  const plain = markdown
    .split('\n')
    .map((line) => line.replace(/^\s*(#+|[-*+]|\d+\.)\s+/, ''))
    .join(' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*_`]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return plain.length <= META_DESCRIPTION_MAX
    ? plain
    : `${plain.slice(0, META_DESCRIPTION_MAX).replace(/\s+\S*$/, '')}…`;
}

/** One local calendar day and the slots that start on it, earliest first. */
type SlotDate = { date: string; slots: EventSlot[] };

/**
 * A membership-gated tier — clubbed into the members-only band. Null is the
 * ungated case (see `EventSlotTicketTier`), so a non-empty list is the whole
 * test.
 */
function isMembersOnlyTier(tier: EventSlotTicketTier): boolean {
  return !!tier.membershipsAppliedToTickettier?.length;
}

/** What the date cell and time chip draw their "AP" badge from. */
function slotHasApTickets(slot: EventSlot): boolean {
  return slot.ticketTiers.some(isMembersOnlyTier);
}

/** A slot time for its chip — no ":00" when the slot lands on the hour. */
function formatTimeChip(datetime: string): string {
  const at = parseISO(datetime);
  return format(at, at.getMinutes() === 0 ? 'h a' : 'h:mm a');
}

/**
 * Group slots by the calendar day they start on, IN THE DEVICE'S ZONE.
 *
 * The day is computed, never sliced off the string. The API sends UTC
 * ("2026-08-20T19:00:00Z"), so the raw date part is the UTC day — which is not
 * the day the user sees for anything late enough in the evening: that slot reads
 * as 12:30 AM on the FOLLOWING day in IST, and a slice would file it under the
 * 20th while its own time chip says half past midnight. Parsing and re-formatting
 * puts the cell in the same zone as every label under it, so the two agree.
 *
 * Dates come back earliest-first, and so do the slots within each — the picker
 * renders them in exactly this order rather than trusting the wire's.
 */
function groupSlotsByLocalDate(slots: EventSlot[]): SlotDate[] {
  const byDate = new Map<string, EventSlot[]>();
  for (const slot of slots) {
    const key = format(parseISO(slot.startAt), 'yyyy-MM-dd');
    const bucket = byDate.get(key);
    if (bucket) bucket.push(slot);
    else byDate.set(key, [slot]);
  }
  // Every `startAt` is UTC in the same format, so a string compare IS a
  // chronological one — no need to build Dates just to sort.
  return [...byDate.entries()]
    .map(([date, daySlots]) => ({
      date,
      slots: daySlots.sort((a, b) => a.startAt.localeCompare(b.startAt)),
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Date + time picker. The date row is a horizontal scroll of one cell per
 * distinct day; picking a day reveals its slots as time chips in the row below.
 * A multi-session day therefore shows several chips — which is why the slots are
 * grouped by date rather than rendered one cell each.
 *
 * It takes the event's OWN `EventSlot[]` and groups them itself. There's one
 * picker in the app and one screen that mounts it, so a bespoke pre-grouped
 * shape would just be a structure the screen has to build for a single consumer;
 * the day-boundary rule (see `groupSlotsByLocalDate`) is the picker's business
 * anyway, since it's the thing that draws the day cells.
 *
 * `selectedSlot` is the single source of truth for both rows: the selected DATE
 * is whichever group contains it, and the selected TIME is its chip. Tapping a
 * date selects that date's first slot; tapping a chip selects that slot. What is
 * WORTH offering is the screen's call — every slot handed here gets a cell, so
 * pass only bookable ones.
 */
function DateTimePicker({
  slots,
  selectedSlot,
  onSelectSlot,
}: {
  slots: EventSlot[];
  selectedSlot: EventSlot | null;
  onSelectSlot: (slot: EventSlot) => void;
}) {
  const slotDates = useMemo(() => groupSlotsByLocalDate(slots), [slots]);

  // The date group the selected slot lives in — drives which date cell reads as
  // selected and which slots the time row shows.
  const selectedDate =
    slotDates.find((d) => d.slots.some((s) => s.id === selectedSlot?.id)) ??
    null;

  return (
    <>
      <YStack gap="$sm">
        <PSText.H1>Available dates</PSText.H1>
        {/* Edge-to-edge date row: Carousel bleeds past the screen's $xl gutter
            (the default) and re-insets the first cell to it, and owns the $md
            gapped row — so this is just the cells. */}
        <Carousel>
          {slotDates.map((group) => {
            return (
              <DateSelector
                key={group.date}
                datetime={group.slots[0].startAt}
                selected={group.date === selectedDate?.date}
                // Selecting a date lands on its first time slot.
                onPress={() => onSelectSlot(group.slots[0])}
                hasApTickets={group.slots.some(slotHasApTickets)}
              />
            );
          })}
        </Carousel>
      </YStack>

      {selectedDate ? (
        <YStack gap={'$sm'}>
          <PSText.H1>Time</PSText.H1>
          {/* Same edge-to-edge row as the dates above — Carousel bleeds past the
              $xl gutter and owns the gapped row, so the chips scroll flush to the
              screen edge instead of a wrapping grid that stops short of it. */}
          <Carousel>
            {selectedDate.slots.map((slot) => (
              <TimeSelector
                key={slot.id}
                label={`${formatTimeChip(slot.startAt)} – ${formatTimeChip(
                  slot.endAt
                )}`}
                selected={slot.id === selectedSlot?.id}
                onPress={() => onSelectSlot(slot)}
                hasApTickets={slotHasApTickets(slot)}
              />
            ))}
          </Carousel>
        </YStack>
      ) : null}
    </>
  );
}

/**
 * The sticky footer for a TICKETED event: ticket count + price on the left, a
 * booking CTA on the right. Render-only — it prints what it's handed and has no
 * auth, cart or lifecycle logic of its own. The cart adds the numbers up
 * (`useEventCart`, which also formats the total: "Free" at zero, never "₹ 0")
 * and the SCREEN picks the button copy ("Book now" vs "Login to book") and the
 * handler.
 *
 * It's only mounted with a non-empty cart, so the count is always ≥ 1 and there
 * is always a real total to show — no empty state to draw here.
 */
function TicketedBookingBar({
  totalTickets,
  priceDisplay,
  buttonTitle,
  onBook,
}: {
  totalTickets: number;
  priceDisplay: string;
  buttonTitle: string;
  onBook: () => void;
}) {
  return (
    <PSBottomBar>
      <YStack>
        <PSText.H1>{priceDisplay}</PSText.H1>
        <PSText.Body3>
          {totalTickets} Ticket{totalTickets === 1 ? '' : 's'}
        </PSText.Body3>
      </YStack>
      <PSButton title={buttonTitle} onPress={onBook} />
    </PSBottomBar>
  );
}
